/**
 * Types shared by the momomail backend (apps/server), desktop shell (apps/desktop) and UI (apps/web):
 * accounts, folders, messages, drafts, settings; the HTTP / WebSocket protocol; the interface exposed by the desktop shell's preload; MomomailBridge used by the UI.
 * Only types and a few pure functions here, no dependency on Node or the DOM.
 */
import { fill, type Vars } from './i18n.js';
import type { Contact } from './contacts.js';

export type Security = 'tls' | 'starttls' | 'none';

export interface ServerConfig {
  host: string;
  port: number;
  security: Security;
}

/** Auth method. v1 only implements password (app-specific password / authorization code); oauth2 is reserved for Gmail / Microsoft later. */
export type AuthKind = 'password' | 'oauth2';

export type ProviderId = 'gmail' | 'outlook' | 'qq' | '163' | 'icloud' | 'custom';

export interface ProviderPreset {
  id: ProviderId;
  label: string;
  /** Detect the provider automatically from the email domain. */
  domains: string[];
  imap: ServerConfig;
  smtp: ServerConfig;
  /** Hint under the password field: whether to use an authorization code or an app-specific password, and where to enable it. */
  passwordLabel: string;
  note: string;
  helpUrl?: string;
  /** Providers that will support OAuth. */
  oauth?: 'google' | 'microsoft';
  color: string;
}

export interface AccountInput {
  id?: string;
  /** Account name in the sidebar, e.g. "工作" (Work), "个人" (Personal). */
  name: string;
  email: string;
  /** Display name used when sending. */
  displayName: string;
  color: string;
  provider: ProviderId;
  imap: ServerConfig;
  smtp: ServerConfig;
  username: string;
  /** Only sent when creating or changing the password; empty means keep the saved one. */
  password?: string;
  authKind: AuthKind;
  signature: string;
}

export interface Account {
  id: string;
  name: string;
  email: string;
  displayName: string;
  color: string;
  provider: ProviderId;
  imap: ServerConfig;
  smtp: ServerConfig;
  username: string;
  authKind: AuthKind;
  signature: string;
  createdAt: number;
  hasSecret: boolean;
}

export type FolderRole = 'inbox' | 'sent' | 'drafts' | 'archive' | 'trash' | 'spam';

export interface Folder {
  id: number;
  accountId: string;
  path: string;
  name: string;
  role: FolderRole | null;
  delimiter: string;
  depth: number;
  selectable: boolean;
  unread: number;
  total: number;
}

export type UnifiedView = 'inbox' | 'flagged' | 'unread';

/**
 * An AI tag as a mailbox: an importance level (important / low / junk), "needs a reply" (only while not answered yet) or one of
 * the user's own tags ("label:" + its name). GET /api/tag-counts → Record<AiTagView, TagCount> (only tags some mail has).
 */
export type AiTagView = 'important' | 'reply' | 'low' | 'junk' | `label:${string}`;
export interface TagCount {
  total: number;
  unread: number;
}

// ---------------- Incremental sync of the client replica (GET /api/sync/changes) ----------------

/** A message in the replica: headers and flags (bodies fetched separately on demand). id matches the backend. */
export interface SyncMessage {
  id: number;
  accountId: string;
  folderId: number;
  uid: number;
  threadId: string;
  messageId: string | null;
  inReplyTo: string | null;
  references: string[];
  emailId: string | null;
  subject: string;
  from: Address;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  replyTo: Address[];
  date: number;
  snippet: string;
  seen: boolean;
  flagged: boolean;
  answered: boolean;
  draft: boolean;
  hasAttachments: boolean;
  size: number;
  /** The AI's tag (null = not tagged). */
  aiTag: AiTag | null;
}

export interface SyncFolder {
  id: number;
  accountId: string;
  path: string;
  name: string;
  delimiter: string;
  role: FolderRole | null;
  selectable: boolean;
}

/**
 * "What changed after version since". If epoch changed (the backend's database was rebuilt) or resync = true: the client clears the replica and starts over from 0.
 * accounts / folders come as full lists when changed (null when unchanged); messages are sorted by version and paginated (when more = true, continue with upTo).
 */
export interface SyncBatch {
  epoch: string;
  upTo: number;
  more: boolean;
  resync: boolean;
  accounts: Account[] | null;
  folders: SyncFolder[] | null;
  messages: SyncMessage[];
  removed: number[];
}

export type MailboxRef =
  | { kind: 'folder'; folderId: number }
  | { kind: 'unified'; view: UnifiedView }
  /** Mail the tagging AI put under one tag, across accounts (not in trash / spam). */
  | { kind: 'tag'; tag: AiTagView }
  | { kind: 'search'; query: string; accountId: string | null };

export interface Address {
  name: string;
  address: string;
}

export interface MessageSummary {
  id: number;
  accountId: string;
  folderId: number;
  threadId: string;
  subject: string;
  from: Address;
  to: Address[];
  date: number;
  snippet: string;
  seen: boolean;
  flagged: boolean;
  answered: boolean;
  draft: boolean;
  hasAttachments: boolean;
  /** Conversation view: message count and unread count in the same conversation (only in conversation view). */
  threadCount: number;
  threadUnread: number;
  /** The AI's tag (null = not tagged: AI tags off, not run yet, or not an inbox message). */
  aiTag: AiTag | null;
}

// ---------------- AI tags ----------------
// Every new inbox message is classified by the AI the user picked for the "tagging" purpose (Settings → AI). The model has no tools
// and no memory; it answers in a fixed format and the server applies it: stores the tag, decides whether / how urgently to notify,
// and (if the user turned it on) moves junk to the spam folder.
// Two kinds of tags: the system's own — importance (level) and needs-reply — and the user's tags (labels): a list they define
// (Settings → AI → Mail tags, or through 馍馍), any number of which can apply to one message.

/**
 * Importance, a system tag: important = they should hear about it now (prominent notification); normal = worth an ordinary
 * notification; low = can wait until they open the app (no notification); junk = ads, spam, scams (no notification; moved to spam if
 * they turned that on).
 */
export type AiLevel = 'important' | 'normal' | 'low' | 'junk';
export const AI_LEVELS: AiLevel[] = ['important', 'normal', 'low', 'junk'];

export interface AiTag {
  level: AiLevel;
  /** The sender expects an answer from the user (system tag). */
  needsReply: boolean;
  /** The user's own tags that fit (names from settings.aiLabels), any number. */
  labels: string[];
  /** One line about what the mail wants (the user's language). */
  summary: string;
  /** Something to put in the calendar, filled in by the AI when it tagged the mail (the "加入日历" button); absent when there is none. */
  event?: AiEvent;
  /** The AI thinks the user would rather unsubscribe from this sender (only for mail with an unsubscribe link, not yet unsubscribed). */
  unsubscribe?: boolean;
  /** Suggestions the user waved away ("不用了"): not shown again. */
  dismissed?: AiSuggestionKind[];
}

export type AiSuggestionKind = 'event' | 'unsubscribe';

/**
 * An event the tagging AI found in a mail. Times as in EventDraft: all-day 'YYYY-MM-DD' (end exclusive), otherwise UTC ISO strings
 * (the AI reads them in the user's time zone, settings.timeZone).
 */
export interface AiEvent {
  title: string;
  location: string;
  description: string;
  allDay: boolean;
  start: string;
  end: string;
  /** In the calendar: added with the button (eventId: the event it made), or found there already (found: true). */
  added?: { at: number; eventId: string | null; found?: boolean };
}

/** The "加入日历" button on a mail (POST /api/messages/:id/ai-event): added, already there, or no calendar to add it to (the client offers its add sheet). */
export type AiEventResult = { status: 'added' | 'already'; event: CalendarEvent | null; tag: AiTag } | { status: 'no-calendar'; event: null; tag: AiTag };

/** One of the user's own tags: its name and, optionally, what it's for (helps the AI). */
export interface AiLabel {
  name: string;
  hint: string;
}

/** Tags a new user starts with (theirs to rename, delete or add to). */
export const DEFAULT_AI_LABELS: AiLabel[] = [
  { name: '工作', hint: '同事、客户、工作上的事' }, // i18n-ok
  { name: '个人', hint: '朋友、家人写来的信' }, // i18n-ok
  { name: '账单订单', hint: '订单、收据、账单、快递、预订' }, // i18n-ok
  { name: '通知', hint: '各种服务和应用的自动通知' }, // i18n-ok
  { name: '订阅', hint: '订阅的新闻、周刊' }, // i18n-ok
];

export interface Attachment {
  index: number;
  filename: string;
  contentType: string;
  size: number;
  inline: boolean;
  cid: string | null;
}

export interface MessageDetail extends MessageSummary {
  cc: Address[];
  bcc: Address[];
  replyTo: Address[];
  messageId: string | null;
  inReplyTo: string | null;
  references: string[];
  folderRole: FolderRole | null;
  folderName: string;
  /** Sanitized HTML (no scripts; remote images depend on allowRemote). null for plain-text mail. */
  html: string | null;
  text: string;
  hasRemoteImages: boolean;
  remoteImagesAllowed: boolean;
  attachments: Attachment[];
  /** Body not fully downloaded yet (offline or download failed). */
  bodyMissing: boolean;
  /** Meeting invite (mail with text/calendar); null if none. */
  invite: CalendarInvite | null;
  /** The sender offers a way to unsubscribe (List-Unsubscribe); null if not. */
  unsubscribe: UnsubscribeInfo | null;
}

// ---------------- 馍馍, the assistant ----------------
// Tap the mascot to chat. The AI picked for "assistant" runs on the server (Claude Code / Codex / Kimi) with only the tools the server
// gives it over a local MCP server: search / read mail and attachments, drafts, moving and marking mail, the calendar, handing small jobs
// to a cheap AI, and its own memory. Sending mail and deleting events wait for the user's OK (an action card in the chat).
// API: GET /api/assistant → AssistantState; POST /api/assistant/messages { text, timeZone } → AssistantState (when the turn is done);
// POST /api/assistant/actions/:id { approve } → AssistantState; DELETE /api/assistant/messages (clear the chat);
// POST /api/assistant/stop → AssistantState (stop the turn being worked on: the reply keeps what it had said);
// POST /api/assistant/presence { client, present, overlay? } (the app the user chats from went to the background / came back: away,
// 馍馍 can't work its screen, finishes with its other tools and leaves a handoff, and a notification tells them when it is done —
// unless overlay: the phone's floating cat stays on screen meanwhile and tells them itself);
// GET /api/assistant/handoffs/:id → the handoff (AffordKit's: a message and calls to run in the app), once;
// GET /api/assistant/memory → { memory } (read-only: the assistant keeps it itself, in its CLI's own memory file).

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  at: number;
  /** Actions this reply is waiting on the user for. */
  actions: string[];
  /** Places in the app the reply points to (shown as buttons that open them). */
  links: AssistantLink[];
  error: boolean;
  /** What a reply did on the way (its tools, in order; absent from older servers and on the user's messages). */
  steps?: AssistantStep[];
  /** How long the reply took, ms (absent from older servers). */
  ms?: number;
  /** What it left to run in the app for a user who was away (an AffordKit handoff's id; GET /api/assistant/handoffs/:id). */
  handoff?: string;
}

/** One thing the assistant did during a turn: a tool, with a few words of what it was about. */
export interface AssistantStep {
  tool: string;
  /** e.g. the words searched for, an event's title (may be absent). */
  detail?: string;
  at: number;
  /** How long it took; absent while it runs. */
  ms?: number;
  error?: boolean;
}

export type AssistantLink = (
  | { kind: 'message'; messageId: number; label: string }
  | { kind: 'calendar'; date: string; label: string }
  | { kind: 'settings'; tab: string; label: string }
  /** A draft it wrote (in the Drafts folder): opens it in compose, for the user to check and send. */
  | { kind: 'draft'; messageId: number; label: string }
  /** A mailbox: a folder, a unified view, an AI tag or search results. */
  | { kind: 'mailbox'; ref: MailboxRef; label: string }
  /** An email in compose, filled in (nothing saved until the user does): new, or a reply / forward of message replyTo (opened first). */
  | { kind: 'compose'; to: string; subject: string; body: string; label: string; mode?: 'new' | 'reply' | 'replyAll' | 'forward'; replyTo?: number }
  /** Into the email being written right now (only the fields given): the user checks it and sends it themselves. */
  | { kind: 'fill-compose'; to?: string; cc?: string; subject?: string; body?: string; label: string }
) & {
  /** Open it right away (the user asked to be taken there), not just offer the button. */
  open?: boolean;
};

export interface AssistantAction {
  id: string;
  kind: 'send-mail' | 'delete-event' | 'tag-rules' | 'tag-labels' | 'unsubscribe' | 'delete-mail' | 'rsvp' | 'clear-cache' | 'remove-account';
  title: string;
  detail: string;
  status: 'pending' | 'done' | 'cancelled' | 'failed';
  result: string;
  at: number;
}

/** An earlier chat, kept in the history (it can be opened again and continued). */
export interface AssistantConversation {
  id: string;
  /** Its first message, shortened. */
  title: string;
  startedAt: number;
  updatedAt: number;
  messages: number;
}

/**
 * What the user has open in the app when they send a chat message, so "this mail", "reply to him", "draft this" mean something:
 * where they are, the message they are reading, the email they are writing. Sent only with a message, never otherwise.
 */
export interface AssistantContext {
  section?: 'mail' | 'calendar';
  /** The mailbox shown (and its name as the user sees it). */
  mailbox?: MailboxRef | null;
  mailboxName?: string;
  /** The message open in the reader. */
  message?: { id: number; subject: string; from: string } | null;
  /** The day the calendar shows (YYYY-MM-DD). */
  calendarDate?: string | null;
  /**
   * What is on screen as text, taken when the message is sent: the open dialog's text (e.g. a settings page) and every field, switch
   * and choice with its current value, unsaved ones included (passwords and keys left out).
   */
  screen?: string;
  /** The email being written: a reply / forward of message replyTo, or new. */
  compose?: { mode: ComposeMode; replyTo: number | null; to: string[]; cc: string[]; subject: string; body: string } | null;
  /** This app's id for the turn (random per run): 馍馍 works its screen through it (assistant-ui events). */
  client?: string;
  /** What is on its screen as an AffordKit tree (AffordContext), when the app has one. */
  ui?: unknown;
}

/** What 馍馍 asks of the app's screen (ServerEvent assistant-ui): the AffordKit tree, or calls to run (affordkit Call). */
export type AssistantUiRequest = { look: true; picture?: boolean } | { calls: unknown[] };

/** The app's answer (POST /api/assistant/ui/:id): the calls' results and the screen after (affordkit ExecuteResult / AffordContext), or why not. */
/** userActions: what the user did on the screen themselves since the last answer (AffordKit UserAction). */
export type AssistantUiAnswer = { results?: unknown[]; context: unknown; picture?: unknown; userActions?: unknown[] } | { error: string; code?: string };

export interface AssistantState {
  /** The current chat only: earlier ones are in conversations. A chat left alone for a while goes there, and the next one starts fresh. */
  messages: AssistantMessage[];
  actions: AssistantAction[];
  busy: boolean;
  step: string | null;
  /** While busy: this turn's steps so far, and what it has said so far (absent from older servers). */
  steps?: AssistantStep[];
  draft?: string;
  /** An AI is available for the assistant. */
  ready: boolean;
  /** Earlier chats, newest first (absent from older servers). */
  conversations?: AssistantConversation[];
}

// ---------------- Unsubscribe ----------------
// POST /api/messages/:id/unsubscribe { confirmMail? } → UnsubscribeResult. One-click links are done by the server; web pages are worked
// by an AI step by step (the server checks and carries out each step); sending an unsubscribe email needs the user's OK (confirm-mail →
// call again with confirmMail: true). Only on the user's tap.

export interface UnsubscribeInfo {
  oneClick: boolean;
  web: boolean;
  mail: boolean;
  /** Already unsubscribed from this sender (when and how). */
  done: { at: number; method: UnsubscribeMethod } | null;
}

export type UnsubscribeMethod = 'one-click' | 'web' | 'mail';

export type UnsubscribeResult =
  | { status: 'done'; method: UnsubscribeMethod; note: string }
  /** Unsubscribing means sending an email from the user's mailbox: ask first. */
  | { status: 'confirm-mail'; to: string; subject: string }
  /** The page needs the user (JavaScript, login, captcha, or no AI): open it in the browser. */
  | { status: 'manual'; url: string; note: string }
  | { status: 'failed'; note: string };

export interface ThreadView {
  threadId: string;
  subject: string;
  messages: MessageDetail[];
}

export interface ComposeAttachment {
  id: string;
  filename: string;
  size: number;
  contentType: string;
  /** Files uploaded to the backend (ids returned by POST /api/uploads). The backend doesn't accept local paths from the client. */
  uploadId?: string;
  /** Include the original message's attachments when forwarding. */
  fromMessage?: { messageId: number; index: number };
}

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward' | 'draft';

export interface Draft {
  id: string;
  accountId: string;
  mode: ComposeMode;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  body: string;
  /** Original text quoted when replying / forwarding (plain text, appended after the body when sending). */
  quoted: string;
  inReplyTo: string | null;
  references: string[];
  attachments: ComposeAttachment[];
  sourceMessageId: number | null;
  /** The corresponding message in the server's Drafts folder (set after saving a draft; saving again replaces it, deleted after sending). */
  serverMessageId: number | null;
  updatedAt: number;
}

export type Theme = 'system' | 'light' | 'dark';

/**
 * Font: all = Momo font everywhere (LXGW Marker Gothic); ui = Momo font for the UI, system font for mail (reading pane, body, compose body);
 * system = system font everywhere.
 */
export type FontMode = 'all' | 'ui' | 'system';

/** UI language code (zh, en, and any other language present in locales/). */
export type Language = string;
/** Language in settings: system = follow system (each device uses its own system language). */
export type LanguagePref = 'system' | Language;

export interface Settings {
  theme: Theme;
  font: FontMode;
  language: LanguagePref;
  notifications: boolean;
  notificationSound: boolean;
  /** Pause notifications until this time (ms); null = not paused; Number.MAX_SAFE_INTEGER means paused until manually resumed. */
  notificationsPausedUntil: number | null;
  launchAtLogin: boolean;
  /** Desktop: the 馍馍 cat walks on the desktop and brings notifications as speech bubbles (off: the usual alert popup). Local to this computer. */
  desktopMascot: boolean;
  conversationView: boolean;
  /** Polling interval when IDLE isn't supported, and for non-inbox folders. */
  pollMinutes: number;
  /**
   * How much content mobile push includes (pushes go through ntfy / Google):
   * full = sender + subject + snippet; sender = sender + subject; count = just "有 N 封新邮件" (N new messages).
   */
  pushPreview: PushPreview;
  /** Cap (MB) on the mail bodies the server caches for me; null = use the server default. Least recently opened are evicted first when exceeded. */
  bodyCacheMb: number | null;
  /** The server prefetches bodies of the last N days of mail in the background (fast to open); 0 = no prefetch, download on open. */
  prefetchDays: number;
  /** AI tags: move what the AI marks as junk to the spam folder (off: only tag it). */
  aiAutoSpam: boolean;
  /** AI tags: the user's own instructions, added to the tagging prompt and taking precedence ("PRs from repo X are junk"). 馍馍 can propose changes. */
  aiTagRules: string;
  /** AI tags: the user's own tags; any number can apply to a message. 馍馍 can propose changes. */
  aiLabels: AiLabel[];
  /** The user's time zone (IANA), set by the apps as they start: the tagging AI reads the times in mail in it. '' = not known yet. */
  timeZone: string;
}

/** Settings → Storage: how much of my cache on the server is used, and the cap. */
export interface StorageInfo {
  usedBytes: number;
  cachedMessages: number;
  totalMessages: number;
  /** The effective cap. */
  capBytes: number;
  /** The server's default cap and the maximum allowed (MB). */
  defaultMb: number;
  maxMb: number;
}

export type PushPreview = 'full' | 'sender' | 'count';

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  font: 'ui',
  language: 'system',
  notifications: true,
  notificationSound: true,
  notificationsPausedUntil: null,
  launchAtLogin: false,
  desktopMascot: true,
  conversationView: true,
  pollMinutes: 5,
  pushPreview: 'full',
  bodyCacheMb: null,
  prefetchDays: 30,
  aiAutoSpam: false,
  aiTagRules: '',
  aiLabels: DEFAULT_AI_LABELS,
  timeZone: '',
};

export type SyncState = 'connecting' | 'syncing' | 'idle' | 'error' | 'offline';

export interface AccountStatus {
  accountId: string;
  state: SyncState;
  error: string | null;
  lastSync: number | null;
  /** The server supports IDLE push. */
  push: boolean;
  /** Login invalid (wrong password / authorization code, or Google authorization revoked): no automatic retry, the user has to log in again. */
  authError: boolean;
}

/** New-mail notification: the backend works it out from settings (toggle, pause, sound); the desktop shell only shows it. */
export interface NotificationPlan {
  title: string;
  body: string;
  /** Which message to open when the notification is clicked (the newest one when there are several). */
  messageId: number;
  silent: boolean;
  /** high: shown prominently (heads-up) — all new mail without AI tags, and what the AI judged urgent; normal: an ordinary, quieter notification. */
  priority: NotifyPriority;
}

export type NotifyPriority = 'high' | 'normal';

/** Events the backend pushes to clients over WebSocket. */
export type ServerEvent =
  /** seq: the database version after the change (the client replica pulls deltas based on it). */
  | { type: 'changed'; accountIds: string[]; seq?: number }
  | { type: 'status'; status: AccountStatus }
  | { type: 'settings'; settings: Settings }
  | { type: 'unread'; total: number }
  | { type: 'new-mail'; accountId: string; messageIds: number[]; notification: NotificationPlan | null }
  /** Calendar accounts / calendars / cached events changed (clients refetch what they show). */
  | { type: 'calendar'; accountIds: string[] }
  /** 馍馍 the assistant is working (step: the tool it's using right now) or done (refetch the chat). */
  | { type: 'assistant'; busy: boolean; step: string | null; steps?: AssistantStep[]; draft?: string }
  /**
   * 馍馍 working the user's screen (AffordKit): for the app that sent the chat message (client), which runs it and answers with
   * POST /api/assistant/ui/:id — look: just what is on screen; calls: AffordKit calls, run in order.
   */
  | { type: 'assistant-ui'; id: string; client: string; request: AssistantUiRequest };

/** Events from the desktop shell (tray, menu, notification clicks) to the page. */
export type ShellEvent =
  | { type: 'open-message'; messageId: number }
  /** The local replica pulled new changes (the UI refreshes as if it received changed). */
  | { type: 'replica'; accountIds: string[]; ready: boolean }
  | { type: 'compose'; to: string | null }
  /** A button of the assistant's reply, from the desktop cat's chat: open that place in the app. */
  | { type: 'open-link'; link: AssistantLink }
  /** The desktop cat leapt into this window and landed at (x, y) (page px, its paws): the page carries on with it (CatReport ready). */
  | { type: 'cat-enter'; x: number; y: number; pose: string; dir: number }
  /** The window is going away: the desktop has the cat again. */
  | { type: 'cat-leave' }
  /** The desktop shell's own settings changed there (e.g. the cat sent home from its menu: desktopMascot off, so it is in its bed). */
  | { type: 'prefs'; prefs: ShellPrefs }
  | { type: 'navigate'; target: 'settings' | 'shortcuts' | 'connection' | 'calendar-today' | 'assistant' }
  /** The desktop auto-update status changed. */
  | { type: 'update'; state: ShellUpdateState };

/**
 * Desktop auto-update (electron-updater, desktop/latest.yml on the download site).
 * disabled = dev build / can't auto-update; latest = already up to date; downloading = downloading in the background; ready = downloaded, restart to install.
 */
export interface ShellUpdateState {
  status: 'disabled' | 'idle' | 'checking' | 'latest' | 'downloading' | 'ready' | 'error';
  /** The currently installed version. */
  current: string;
  /** The new version (present when downloading / ready). */
  version: string | null;
  /** Download progress 0–100. */
  percent: number | null;
  error: string | null;
  /** Time of the last check (ms). */
  checkedAt: number | null;
}

/** All events the page receives. */
export type MailEvent = ServerEvent | ShellEvent | { type: 'toast'; text: string; kind: 'info' | 'ok' | 'error' };

export interface ListOptions {
  ref: MailboxRef;
  filter: 'all' | 'unread' | 'flagged';
  /** Pagination cursor: the last loaded message (newest first). */
  before: { date: number; id: number } | null;
  limit: number;
  /** A specific account (null in unified view, meaning all accounts). */
  accountId: string | null;
}

export interface ListResult {
  messages: MessageSummary[];
  hasMore: boolean;
}

export type MessageAction =
  | { kind: 'seen'; value: boolean }
  | { kind: 'flagged'; value: boolean }
  | { kind: 'archive' }
  | { kind: 'trash' }
  | { kind: 'spam' }
  | { kind: 'move'; folderId: number }
  | { kind: 'deleteForever' };

export interface ActScope {
  conversation: boolean;
  ref: MailboxRef;
}

export interface TestResult {
  imap: { ok: boolean; error: string | null };
  smtp: { ok: boolean; error: string | null };
}

export interface SendResult {
  ok: boolean;
  error: string | null;
  /** Show a hint when it wasn't saved to Sent (server rejected APPEND). */
  warning: string | null;
}


// ---------------- HTTP / WebSocket protocol ----------------

/** HTTP API version (returned by GET /api/health; bumped on incompatible changes). */
export const API_VERSION = 2;

// ---------------- Mobile push ----------------
//
// After logging in, the mobile app registers itself with the backend (PUT /api/push/devices/:deviceId), with the channels it can receive push on:
// - unifiedpush: when the phone has a UnifiedPush distributor such as ntfy installed, the push endpoint given by the distributor (usually https://ntfy…/up… on a self-hosted ntfy),
//   plus the public key for WebPush encryption (RFC 8291); the backend encrypts and POSTs to this endpoint, and the distributor delivers it to the phone over its own long-lived connection, bypassing Google.
// - fcm: the token Firebase provides when Google services are available; the backend sends "data messages" (high priority) via FCM HTTP v1.
// - jpush: JPush RegistrationID (Chinese phones use vendor channels like Xiaomi / Huawei / vivo / OPPO through JPush, received even when the app is killed).
//   ⚠ Vendor channels require registering the app on each vendor's open platform, which almost always requires an enterprise developer (business license); individual developers can't use them.
//   The JPush AppKey is built into the app (applied for by whoever publishes the app), and only a server holding the matching Master Secret can send:
//   your own server configures it directly; other people's self-hosted servers will later have to go through the publisher's push relay service.
// - keepalive: the app keeps its own background connection (Android foreground service, WebSocket /api/ws?push=<deviceId>, see push-socket.ts in server),
//   used when Google push can't get through. No extra app to install; the cost is a persistent "正在后台运行" (Running in background) notification and a bit more battery.
// On new mail the backend sends to each device's channels: devices with JPush use only JPush (JPush notifications are shown by the system / JPush SDK and can't be deduplicated against the app's own);
// other devices still get a UnifiedPush copy; FCM vs. the background connection is "decided at send time": devices with the background connection get FCM first,
// and if the phone doesn't confirm receipt within a few seconds (e.g. Google is unreachable right now), it switches to the background connection. PushPayload carries the same id; the app dedupes by id and shows the notification itself.
// Devices are bound to the login session: on logout / session expiry, pushes stop and the registration is deleted.
//
// API: GET /api/push/config → PushConfig; GET /api/push/devices → PushDevice[];
//       PUT /api/push/devices/:deviceId（PushDeviceInput）→ PushDevice；DELETE /api/push/devices/:deviceId；
//       POST /api/push/test { deviceId? } → PushTestResult[] (sends to all devices when no deviceId is given);
//       POST /api/push/devices/:deviceId/ack { id }: the phone received this push via FCM (so the backend won't use the background connection)

export interface UnifiedPushTarget {
  endpoint: string;
  /** WebPush encryption: p256dh public key (base64url, 65-byte uncompressed point) and auth secret (base64url, 16 bytes). Sent in plaintext if neither is present. */
  p256dh?: string;
  auth?: string;
}

export interface PushDeviceInput {
  /** Device name, for display (e.g. "Pixel 8"). The device id is in the URL: the app generates a random UUID on first launch and stores it. */
  name: string;
  platform: 'android' | 'ios';
  unifiedpush: UnifiedPushTarget | null;
  fcmToken: string | null;
  /** JPush RegistrationID (only present when the app was built with a JPush AppKey; old app versions don't send it). */
  jpushId?: string | null;
  /** The app's own background connection is enabled (old app versions don't send it). */
  keepalive?: boolean;
}

export interface PushDevice extends PushDeviceInput {
  id: string;
  createdAt: number;
  lastSeen: number;
  /** Reason for the most recent push failure (per channel). */
  lastError: { unifiedpush: string | null; fcm: string | null; jpush: string | null; keepalive: string | null };
  /** The background connection is currently connected. */
  connected: boolean;
}

/** GET /api/push/config: which push channels the backend supports, plus the public config the app needs to initialize Firebase (so google-services.json doesn't have to be bundled into the app). */
export interface PushConfig {
  fcm: { projectId: string; appId: string; apiKey: string; messagingSenderId: string } | null;
  /** Recommended ntfy server (the one deployed alongside); the app tells the user to use it in ntfy. */
  ntfyUrl: string | null;
  /** The backend has JPush configured (the app registers its RegistrationID only if it has JPush). Old backends don't have this field. */
  jpush?: boolean;
}

/** Per channel: null = this device doesn't have the channel, 'ok' = the push service accepted it, otherwise the error reason. */
export interface PushTestResult {
  deviceId: string;
  unifiedpush: string | null;
  fcm: string | null;
  jpush: string | null;
  /** Background connection: 'ok' = the phone confirmed receipt; 'queued' = not connected now, will be resent on reconnect; 'skipped' = FCM already delivered it, not needed. */
  keepalive: string | null;
}

/** Messages on the background connection (/api/ws?push=…): the backend sends push and heartbeat ping (the phone uses it to detect disconnects), the phone replies with ack. */
export type PushSocketFrame = { kind: 'push'; payload: PushPayload } | { kind: 'ping' } | { kind: 'ack'; id: string };

/**
 * Push content (the UnifiedPush body is this JSON; FCM data has the same fields, with all values converted to strings).
 * Same id means the same notification (one copy from each of two channels); the app shows it only once.
 */
export type PushPayload =
  | { v: 1; kind: 'new-mail'; id: string; title: string; body: string; accountId: string; messageId: number; count: number; unread: number; silent: boolean; priority: NotifyPriority; at: number }
  | { v: 1; kind: 'test'; id: string; title: string; body: string; at: number }
  /** Alerts the user needs to act on (e.g. a mailbox's login has become invalid). Empty accountId means not specific to any account. */
  | { v: 1; kind: 'alert'; id: string; title: string; body: string; accountId: string; at: number }
  /**
   * 馍馍 finished while the user was out of the app (its reply, in short). handoff: what to run in the app when it is tapped, by id
   * (GET /api/assistant/handoffs/:id; the notification carries only the id: anyone could make a phone open the app with a payload).
   */
  | { v: 1; kind: 'assistant'; id: string; title: string; body: string; handoff?: string; at: number };

/** GET /api/health (no login required). */
export interface HealthInfo {
  ok: true;
  name: 'momomail';
  apiVersion: number;
  version: string;
  /** token: local mode, a random token given by the desktop shell when it starts the backend; password: server mode, first POST /api/login to get a session token. */
  auth: 'token' | 'users';
  /** Whether users can reset via "忘记密码" (Forgot password) themselves (the server has a public URL and a system sender mailbox configured). */
  selfReset: boolean;
}

/** Response of POST /api/login. */
export interface LoginResult {
  token: string;
  expiresAt: number;
}

// ---------------- Users (multi-user servers) ----------------
//
// No open registration: the admin generates a one-time invite link (https://<server>/#/invite/<token>), and the invitee sets their own username, password and recovery email.
// Forgot password: POST /api/password/forgot { login: username or recovery email } → sends https://<server>/#/reset/<token> to the recovery email (30 minutes, single use);
// admins can also generate reset links (without seeing the password). Passwords are salted scrypt hashes; tokens are stored in the database only as SHA-256.
//
// API: POST /api/login { username, password } → LoginResult; GET /api/me → Me; PATCH /api/me { recoveryEmail };
//       POST /api/me/password { current, next } (logins on other devices are signed out);
//       GET /api/invites/:token → InviteInfo；POST /api/invites/:token/accept { username, password, recoveryEmail } → LoginResult；
//       POST /api/password/forgot { login }；POST /api/password/reset { token, password }；
//   Admin: GET /api/admin/users → AdminUser[]; PATCH /api/admin/users/:id { disabled?, isAdmin? }; DELETE /api/admin/users/:id;
//       POST /api/admin/users/:id/reset-link → IssuedLink；GET /api/admin/invites → PendingInvite[]；
//       POST /api/admin/invites { isAdmin, note } → IssuedLink；DELETE /api/admin/invites/:id；
//       GET /api/admin/mailer → MailerInfo; PUT /api/admin/mailer { accountId: one of the admin's own mail accounts | null }

export interface Me {
  id: string;
  username: string;
  isAdmin: boolean;
  recoveryEmail: string | null;
  /** Desktop local mode: no user system. */
  local: boolean;
}

export interface AdminUser {
  id: string;
  username: string;
  isAdmin: boolean;
  disabled: boolean;
  recoveryEmail: string | null;
  createdAt: number;
  lastLogin: number | null;
  accounts: number;
}

export interface InviteInfo {
  valid: boolean;
  expiresAt: number | null;
  isAdmin: boolean;
}

export interface PendingInvite {
  id: string;
  note: string;
  isAdmin: boolean;
  createdAt: number;
  expiresAt: number;
}

/** A freshly generated invite / reset link (returned only this once; the database only has the hash). */
export interface IssuedLink {
  url: string;
  expiresAt: number;
}

export interface MailerInfo {
  /** The mailbox used for system mail (password recovery emails are sent from it). */
  mailer: { username: string; accountId: string; email: string } | null;
  /** The server has a public URL configured (MOMOMAIL_PUBLIC_URL); without it, reset emails can't be sent. */
  publicUrl: string | null;
}

/** On error, HTTP returns { error, code } (error is in Chinese and can be shown to the user directly). */
export interface ApiError {
  error: string;
  code: 'unauthorized' | 'forbidden' | 'not-found' | 'bad-request' | 'too-large' | 'rate-limited' | 'server-error';
}

/**
 * Frames sent by the backend over WebSocket (/api/ws).
 * - Pass the token via subprotocol when connecting: new WebSocket(url, ['momomail.v2', 'bearer.' + token]); Node clients can also use the Authorization header.
 * - When reconnecting, include ?boot=<boot from the last hello>&since=<last received seq>: if the backend still has the events after that, it replays them first (hello.resumed = true),
 *   otherwise resumed = false and the client should refetch all state.
 */
export type WsFrame =
  | { kind: 'hello'; boot: string; seq: number; resumed: boolean; unread: number; statuses: AccountStatus[]; settings: Settings }
  | { kind: 'event'; seq: number; event: ServerEvent }
  | { kind: 'ping'; time: number };

export const WS_PROTOCOL = 'momomail.v2';

// ---------------- Utilities (used by backend, desktop shell and UI) ----------------

export function notificationsPaused(settings: Pick<Settings, 'notificationsPausedUntil'>, now = Date.now()): boolean {
  return settings.notificationsPausedUntil != null && settings.notificationsPausedUntil > now;
}

/** Tray tooltip text. */
export function trayTooltip(unread: number, paused: boolean, offline = false, tr: (zh: string, vars?: Vars) => string = fill): string {
  if (offline) return tr('馍馍邮箱 · 连不上邮件服务');
  const base = unread > 0 ? tr('馍馍邮箱 · {n} 封未读', { n: unread }) : tr('馍馍邮箱 · 没有未读邮件');
  return paused ? tr('{base}（通知已暂停）', { base }) : base;
}

// ---------------- Desktop shell ----------------

/** How the UI connects to the backend. */
export interface ConnectionInfo {
  /** local: a local backend started by the desktop shell itself; remote: the desktop shell connects to a remote server; browser: the web version opened in a regular browser. */
  mode: 'local' | 'remote' | 'browser';
  /** Backend URL, e.g. http://127.0.0.1:53121 or https://mail.example.com; empty when remote mode isn't set up yet. */
  url: string;
  /** Present in local mode and logged-in remote mode; login is required when absent. */
  token: string | null;
  /** E.g. the local backend failed to start. */
  error: string | null;
}

export type ConnectionState = 'connecting' | 'online' | 'reconnecting' | 'auth-required';

/** Settings that only concern this computer and aren't stored on the backend. */
export interface ShellPrefs {
  launchAtLogin: boolean;
  /** The desktop cat is shown (default on). */
  desktopMascot?: boolean;
  /** The language actually used by the UI (the page tells the desktop shell, so the tray menu, system notifications and dialogs switch too). */
  language?: Language;
  /** Remote-mode local replica: keep the message list for the last N days. */
  replicaDays?: number;
  /** Remote-mode local replica: max MB for the content of opened messages (0 = don't cache content). */
  replicaCacheMb?: number;
}

/** Current state of the desktop remote-mode local replica (Settings → Storage). */
export interface ReplicaStatus {
  ready: boolean;
  syncing: boolean;
  lastSync: number | null;
  error: string | null;
  days: number;
  cacheMb: number;
  messages: number;
  cachedThreads: number;
  cacheBytes: number;
  fileBytes: number | null;
}

/**
 * Desktop remote mode: the local replica in the main process (apps/server/src/replica.ts). Lists, folders and unread counts are read from here (instant, readable offline),
 * opened messages are cached; actions update the replica first, then go to the backend. Absent (null) in local mode and the web version.
 */
export interface ShellReplica {
  status(): Promise<ReplicaStatus | null>;
  list(options: ListOptions, conversation: boolean): Promise<ListResult>;
  folders(accountId: string | null): Promise<Folder[]>;
  counts(): Promise<Record<UnifiedView, number>>;
  accounts(): Promise<Account[]>;
  /** The day the replica starts from (ask the backend for anything earlier). */
  windowStart(): Promise<number>;
  thread(messageId: number, conversation: boolean, allowRemote: boolean): Promise<ThreadView>;
  prefetch(ids: number[], conversation: boolean): Promise<void>;
  /** Optimistic update: change the replica first, return the affected message ids (reverted if the backend fails). */
  apply(ids: number[], action: MessageAction, scope: ActScope | null): Promise<number[]>;
  markAllRead(ref: MailboxRef, accountId: string | null, onServer: boolean): Promise<void>;
  revert(ids: number[]): Promise<void>;
  configure(patch: { days?: number; cacheMb?: number }): Promise<ReplicaStatus | null>;
  /** Clear the local replica and sync from scratch. */
  reset(): Promise<ReplicaStatus | null>;
}

/** The desktop shell's preload exposes this as window.momomailShell: only "shell" capabilities like window, files and connection info, no mail operations. */
export interface ShellBridge {
  apiVersion: 2;
  platform: string;
  version: string;
  quit(): void;
  window: {
    minimize(): void;
    toggleMaximize(): void;
    /** Close = hide to tray. */
    close(): void;
    isMaximized(): Promise<boolean>;
    onMaximized(callback: (value: boolean) => void): () => void;
  };
  /** In local mode, waits until the backend has started before returning. */
  connection(): Promise<ConnectionInfo>;
  /**
   * Switch to a remote server: the desktop shell (main process) logs in to url with username + password, on success stores the session token with system encryption, and the page reloads.
   * Login is done by the main process; the page only talks to the already-configured address (the CSP only allows it).
   */
  connectRemote(url: string, username: string, password: string): Promise<void>;
  /** Go back to the local backend; the page reloads afterwards. */
  useLocal(): Promise<void>;
  /** Clear the saved token when the remote token is invalid or on logout. */
  forgetToken(): Promise<void>;
  openExternal(url: string): void;
  /** Download an attachment from the backend and open it with the system app (warns first for executables). */
  openAttachment(messageId: number, index: number, filename: string): Promise<void>;
  /** Download an attachment from the backend with a "Save As" dialog. */
  saveAttachment(messageId: number, index: number, filename: string): Promise<boolean>;
  prefs: {
    get(): Promise<ShellPrefs>;
    set(patch: Partial<ShellPrefs>): Promise<ShellPrefs>;
  };
  /** Remote-mode local replica (absent in old desktop shells). */
  replica?: ShellReplica;
  /** Auto-update (absent in old desktop shells). Status changes are pushed as update events via onEvent. */
  updates?: {
    state(): Promise<ShellUpdateState>;
    /** Check now (downloads in the background if there's a new version). */
    check(): Promise<ShellUpdateState>;
    /** Downloaded: quit and install, reopening automatically after install. */
    install(): void;
  };
  onEvent(callback: (event: ShellEvent) => void): () => void;
  /** The desktop cat in this window (absent in older desktop shells). */
  cat?: { report(report: CatReport): void };
  /** What the user has open (for the chat over the desktop cat, which the main process sends). */
  assistantContext?(context: AssistantContext): void;
}

/**
 * The page to the desktop shell about the cat in its window: where a leaping cat can land (spot, while it is out on the desktop),
 * that it took over a cat that landed (ready), and where it is (state, often: so the desktop can put it there when the window goes);
 * that the user dropped it into its bed (home: it stays there, the desktop cat goes off) or picked it up out of the bed (out: the page
 * holds it, the desktop cat comes on but stays in this window).
 */
export type CatReport =
  | { type: 'spot'; x: number; y: number }
  | { type: 'ready' }
  | { type: 'state'; x: number; y: number; dir: number }
  | { type: 'home' }
  | { type: 'out'; x: number; y: number };

/** The unified interface used by the UI (implemented over HTTP + WebSocket in apps/web/src/lib/bridge.ts, same on desktop and in the browser). */
// ---------------- AI (agent): Claude Code / Codex / Kimi CLI, each user logs in themselves ----------------

/** The five providers on the cards: Qwen and DeepSeek go through Kimi CLI's OpenAI-compatible API; Claude, Codex and Kimi use their own CLIs. */
export type AiProviderId = 'qwen' | 'deepseek' | 'claude' | 'codex' | 'kimi';
/** One connection method of a provider: <provider>-api (API Key) or <provider>-login (web login, using an existing subscription). */
export type AiAppConnectionId = 'qwen-api' | 'deepseek-api' | 'claude-api' | 'codex-api' | 'kimi-api' | 'claude-login' | 'codex-login' | 'kimi-login';
/**
 * Already present locally (on the machine the backend runs on): <provider>-local-login = Claude Code / Codex / Kimi already logged in on the system,
 * <provider>-local-api = an API Key in environment variables (or Claude Code config). Only counts as consent once the user selects it.
 */
export type AiLocalConnectionId = 'qwen-local-api' | 'deepseek-local-api' | 'claude-local-api' | 'codex-local-api' | 'kimi-local-api' | 'claude-local-login' | 'codex-local-login' | 'kimi-local-login';
export type AiConnectionId = AiAppConnectionId | AiLocalConnectionId;

export interface AiModel {
  id: string;
  label: string;
  context: number;
}

export interface AiConnection {
  id: AiConnectionId;
  provider: AiProviderId;
  /** api: enter an API Key; login: web login (existing subscription). */
  method: 'api' | 'login';
  label: string;
  /** The server has this CLI (can't be used if not installed). */
  available: boolean;
  /** API Key saved. */
  configured: boolean;
  /** Web login completed. */
  loggedIn: boolean;
  model: string | null;
  context: number | null;
  baseUrl: string | null;
  /** Optional official API endpoints (China / international). */
  baseUrls: string[];
  models: AiModel[];
  /** Local connection (uses an existing login / API Key on the system). */
  local: boolean;
  /** Local connection: currently detected (unused local connections are only listed when detected). */
  localAvailable: boolean;
  /** The detected source ("本机 Claude Code 登录" (local Claude Code login), "ANTHROPIC_API_KEY" and the like), not the credential itself. */
  localSource: string | null;
}

export type AiPhase = 'idle' | 'logging-in' | 'verifying' | 'success' | 'error' | 'logging-out' | 'running';

/** The currently selected AI and any login / verification in progress. */
export interface AiStatus {
  /** The selected connection (null = none selected yet). */
  connectionId: AiConnectionId | null;
  phase: AiPhase;
  /** The selected connection is usable (logged in or has a Key). */
  ready: boolean;
  /** The AI's answer when verification succeeded. */
  reply: string;
  error: string;
  /** The connection currently doing web login, plus the authorization URL to open and the code to enter. */
  loginFor: AiConnectionId | null;
  url: string;
  code: string;
  /** Claude: after authorizing, the web page shows a code that has to be pasted back. */
  needsCode: boolean;
}

export interface AiOverview {
  connections: AiConnection[];
  status: AiStatus;
  /** Which AI does what (a purpose without an entry is off). */
  purposes: Partial<Record<AiPurpose, AiAssignment>>;
  tagging: AiTaggingStatus;
}

/**
 * What an AI can be picked for. tagging: classify every new inbox message (cheap, fast models suit it);
 * unsubscribe: work unsubscribe web pages when the user taps the button (falls back to the tagging AI, then the selected one);
 * assistant: 馍馍, the personal assistant you chat with (a capable model suits it; falls back to the selected one).
 */
export type AiPurpose = 'tagging' | 'unsubscribe' | 'assistant';
export const AI_PURPOSES: AiPurpose[] = ['tagging', 'unsubscribe', 'assistant'];

export interface AiAssignment {
  connectionId: AiConnectionId;
  /** null = the connection's own model. */
  model: string | null;
}

export interface AiTaggingStatus {
  /** Messages waiting to be tagged. */
  pending: number;
  /** Messages tagged so far, and today. */
  tagged: number;
  today: number;
  lastRun: number | null;
  lastError: string | null;
}

export interface AiRunResult {
  text: string;
  connectionId: AiConnectionId;
  model: string | null;
  durationMs: number;
}

export interface AiBridge {
  overview(): Promise<AiOverview>;
  /** Select a connection that's already logged in / has a Key. */
  select(id: AiConnectionId): Promise<AiOverview>;
  /** Start web login (runs the CLI's login flow on the server and gives the UI the authorization URL and code). */
  login(id: AiConnectionId): Promise<AiOverview>;
  /** Claude web login: hand back the code shown on the web page. */
  submitCode(code: string): Promise<AiOverview>;
  cancelLogin(): Promise<AiOverview>;
  /** Use the existing local login / API Key (selecting it is consent), and select it. */
  useLocal(id: AiConnectionId): Promise<AiOverview>;
  /** Save the API Key (and select it). When apiKey is empty, only change the model / endpoint. */
  configure(input: { id: AiConnectionId; apiKey?: string; model?: string; context?: number; baseUrl?: string }): Promise<AiOverview>;
  /** Log out of web login / delete the API Key; for local connections, just stop using it (the system's login is left alone). */
  logout(id: AiConnectionId): Promise<AiOverview>;
  /** Ask the selected AI a simple question to confirm it works. */
  verify(): Promise<AiOverview>;
  /** Have the selected AI answer a prompt (no tools at all, text only). */
  run(prompt: string): Promise<AiRunResult>;
  /** Pick the AI (and model) for a purpose; null turns the purpose off. */
  assign(purpose: AiPurpose, assignment: AiAssignment | null): Promise<AiOverview>;
  /** Tag the newest inbox messages again (after changing the tags or instructions). */
  retag(): Promise<AiOverview>;
}

export interface AssistantBridge {
  state(): Promise<AssistantState>;
  /** context: what the user has open (see AssistantContext). */
  send(text: string, timeZone: string, context?: AssistantContext): Promise<AssistantState>;
  act(actionId: string, approve: boolean): Promise<AssistantState>;
  /** Stop the turn being worked on (the reply keeps what it had said). */
  stop(): Promise<AssistantState>;
  /** Start a new chat (the current one goes to the history). */
  clear(): Promise<AssistantState>;
  /** Continue an earlier chat (the current one goes to the history). */
  openConversation(id: string): Promise<AssistantState>;
  /** Delete an earlier chat for good. */
  deleteConversation(id: string): Promise<AssistantState>;
  /** What 馍馍 remembers (its memory file; it keeps it up to date itself). */
  memory(): Promise<string>;
  /** This window's answer to 馍馍 working its screen (ServerEvent assistant-ui with this window's client id). */
  uiAnswer(id: string, answer: AssistantUiAnswer): Promise<void>;
  /** This window was hidden / came back (client: its id): away, 馍馍 finishes without its screen. */
  presence(client: string, present: boolean): Promise<void>;
}

/** Settings → Storage. */
export interface StorageBridge {
  /** My body cache on the server. */
  server(): Promise<StorageInfo>;
  clearServer(): Promise<StorageInfo>;
  /** The desktop remote-mode local replica; null if none. */
  replica(): Promise<ReplicaStatus | null>;
  configureReplica(patch: { days?: number; cacheMb?: number }): Promise<ReplicaStatus | null>;
  resetReplica(): Promise<ReplicaStatus | null>;
}

export interface MomomailBridge {
  apiVersion: 2;
  platform: string;
  /** Inside the desktop shell (has window buttons, tray, local backend). */
  desktop: boolean;
  app: {
    version: string;
    /** Actually quit (not hide to tray). Does nothing in the browser. */
    quit(): void;
  };
  /** null in the browser. */
  window: ShellBridge['window'] | null;
  connection: {
    info(): ConnectionInfo;
    state(): ConnectionState;
    onState(callback: (state: ConnectionState) => void): () => void;
    /** Remote server: log in with username + password (the desktop shell remembers the token, and the page reloads). */
    login(url: string, username: string, password: string): Promise<void>;
    logout(): Promise<void>;
    /** Desktop shell: go back to the local backend. */
    useLocal(): Promise<void>;
  };
  accounts: {
    list(): Promise<Account[]>;
    presets(): Promise<ProviderPreset[]>;
    test(input: AccountInput): Promise<TestResult>;
    save(input: AccountInput): Promise<Account>;
    remove(id: string): Promise<void>;
    reorder(ids: string[]): Promise<void>;
    status(): Promise<AccountStatus[]>;
  };
  mail: {
    folders(accountId: string | null): Promise<Folder[]>;
    unifiedCounts(): Promise<Record<UnifiedView, number>>;
    /** How much mail each AI tag has (tags no mail has are left out). */
    tagCounts(): Promise<Partial<Record<AiTagView, TagCount>>>;
    /** Move everything the tagging AI called junk to spam; how many. */
    junkToSpam(): Promise<number>;
    list(options: ListOptions): Promise<ListResult>;
    /** Recipient suggestions when composing: the backend picks the contacts that best match query from all correspondence; exclude is the addresses already filled in. */
    contacts(query: string, exclude: string[]): Promise<Contact[]>;
    thread(messageId: number, conversation: boolean, allowRemote: boolean): Promise<ThreadView>;
    /** When conversation is true, act on all messages in the same scope (ref) within the conversations these messages belong to. */
    act(messageIds: number[], action: MessageAction, scope: ActScope | null): Promise<void>;
    /** The unsubscribe button (confirmMail: the user agreed to send the unsubscribe email). */
    unsubscribe(messageId: number, confirmMail: boolean): Promise<UnsubscribeResult>;
    /** After 'manual': the user says they finished on the page (POST /api/messages/:id/unsubscribe/done). */
    unsubscribeDone(messageId: number): Promise<UnsubscribeResult>;
    /** The AI's "加入日历" on a mail: add its event to the calendar (POST /api/messages/:id/ai-event), or take it out again (DELETE). */
    addAiEvent(messageId: number): Promise<AiEventResult>;
    undoAiEvent(messageId: number): Promise<AiTag>;
    /** Wave away one of the AI's suggestions on a mail (POST /api/messages/:id/ai-dismiss { kind }). */
    dismissAi(messageId: number, kind: AiSuggestionKind): Promise<AiTag>;
    /** onServer: the entire folder on the server (in unified view, each account's inbox), including old mail not yet downloaded. */
    markAllRead(ref: MailboxRef, accountId: string | null, onServer?: boolean): Promise<void>;
    sync(accountId: string | null): Promise<void>;
    /** Prefetch the content of these messages (likely to be opened next in the list), without waiting for the result. */
    prefetch(messageIds: number[], conversation: boolean): void;
    openAttachment(messageId: number, index: number, filename: string): Promise<void>;
    saveAttachment(messageId: number, index: number, filename: string): Promise<boolean>;
    openExternal(url: string): Promise<void>;
  };
  compose: {
    create(mode: ComposeMode, sourceMessageId: number | null, accountId: string | null): Promise<Draft>;
    saveLocal(draft: Draft): Promise<void>;
    saveDraft(draft: Draft): Promise<Draft>;
    discard(draft: Draft): Promise<void>;
    send(draft: Draft): Promise<SendResult>;
    /** Open a file picker and upload to the backend. */
    pickFiles(): Promise<ComposeAttachment[]>;
    /** Upload dragged-in files. */
    upload(files: File[]): Promise<ComposeAttachment[]>;
    localDrafts(): Promise<Draft[]>;
  };
  settings: {
    get(): Promise<Settings>;
    update(patch: Partial<Settings>): Promise<Settings>;
  };
  /** Google sign-in (when the server has an OAuth client configured): add Gmail, reauthorize, connect Google Calendar. */
  storage: StorageBridge;
  ai: AiBridge;
  assistant: AssistantBridge;
  google: {
    info(): Promise<OAuthInfo>;
    start(purpose: OAuthPurpose, options: { loginHint?: string; accountId?: string }): Promise<OAuthStart>;
    flow(flowId: string): Promise<OAuthFlowState>;
    /** When the callback URL can't be opened (loopback mode, browser not on the server): paste back the redirected URL. */
    finish(flowId: string, response: string): Promise<OAuthFlowState>;
  };
  calendar: {
    info(): Promise<CalendarInfo>;
    disconnect(email: string): Promise<void>;
    /** Guess an event from a message (times interpreted in timeZone). */
    suggest(messageId: number, timeZone: string): Promise<EventSuggestion>;
    /** Write directly into the connected Google Calendar. */
    add(event: EventDraft, messageId: number | null): Promise<CalendarAddResult>;
    /** Respond to a meeting invite (sends an iMIP reply to the organizer; also updates the calendar if Google Calendar is connected). */
    respond(messageId: number, status: RsvpStatus): Promise<RsvpResult>;
    connectCaldav(input: CaldavInput): Promise<CalendarAccount>;
    removeAccount(id: string): Promise<void>;
    syncAccount(id: string): Promise<void>;
    updateCalendar(id: string, patch: { hidden?: boolean; color?: string }): Promise<CalendarSource>;
    /** Cached events of the visible calendars overlapping [from, to) (ISO strings or YYYY-MM-DD). */
    events(from: string, to: string): Promise<CalendarEvent[]>;
    createEvent(input: CalendarEventInput): Promise<CalendarEvent>;
    updateEvent(id: string, input: CalendarEventInput): Promise<CalendarEvent>;
    deleteEvent(id: string): Promise<void>;
  };
  onEvent(callback: (event: MailEvent) => void): () => void;
}

// ---------------- Google sign-in (OAuth) ----------------
//
// Available when the server has a Google OAuth client configured (MOMOMAIL_GOOGLE_CLIENT_ID / SECRET). Two callback modes:
// - redirect: with a public URL configured, Google redirects straight back to <public URL>/api/oauth/google/callback, the backend completes it, and the client polls the flow status;
// - loopback: no public URL configured (local / dev), redirects to http://127.0.0.1:53682/. When the browser and backend are on the same machine, the backend catches it itself;
//   otherwise the browser shows "can't be reached", and you paste the URL from the address bar back (finish).
// API: GET /api/oauth → OAuthInfo; POST /api/oauth/google/start { purpose, loginHint?, accountId? } → OAuthStart;
//       GET /api/oauth/google/flows/:flowId → OAuthFlowState；POST /api/oauth/google/finish { flowId, response } → OAuthFlowState

export type OAuthPurpose = 'mail' | 'calendar';

export interface OAuthInfo {
  /** The server has a Google OAuth client configured. */
  google: boolean;
  /** The URL Google redirects back to after authorization (the admin must register it in Google Cloud). */
  redirectUri: string | null;
}

export interface OAuthStart {
  flowId: string;
  /** The Google authorization URL to open in the browser. */
  url: string;
  redirectUri: string;
  mode: 'redirect' | 'loopback';
}

export interface OAuthFlowState {
  flowId: string;
  purpose: OAuthPurpose;
  status: 'pending' | 'done' | 'error';
  error: string | null;
  /** The authorized Google account. */
  email: string | null;
  /** purpose = mail: the account created or reauthorized. */
  accountId: string | null;
}

// ---------------- Calendar ----------------
//
// Time format: all-day → 'YYYY-MM-DD' (end exclusive, same as iCalendar / Google); otherwise → a UTC ISO string.
// Without a connected Google Calendar, the client uses googleCalendarUrl() to open Google Calendar's "new event" page (pre-filled), or exports an .ics.
// API: GET /api/calendar → CalendarInfo; POST /api/calendar/connect { loginHint? } → OAuthStart; DELETE /api/calendar/links/:email;
//       POST /api/messages/:id/calendar/suggest { timeZone } → EventSuggestion；POST /api/calendar/events { event, messageId? } → CalendarAddResult；
//       POST /api/messages/:id/invite/respond { status } → RsvpResult

export type RsvpStatus = 'accepted' | 'tentative' | 'declined';

export interface CalendarInvite {
  /** REQUEST (invite / update), CANCEL (cancellation), REPLY (someone's reply), PUBLISH (just an event). */
  method: string;
  uid: string;
  summary: string;
  start: string;
  end: string;
  allDay: boolean;
  /** Original time zone (IANA), null if none. */
  timeZone: string | null;
  location: string;
  description: string;
  organizer: Address | null;
  attendees: (Address & { status: RsvpStatus | 'needs-action' | null })[];
  /** Recurring event (shows "重复" (Repeats); the time is the first occurrence). */
  recurring: boolean;
  cancelled: boolean;
  /** My reply if already sent (in momomail), or my status as written in the invite. */
  myStatus: RsvpStatus | null;
  /** Whether a reply is possible (it's an invite, has an organizer, and I'm not the organizer). */
  canRespond: boolean;
}

export interface EventDraft {
  title: string;
  location: string;
  description: string;
  allDay: boolean;
  start: string;
  end: string;
  /** The user's time zone (IANA), used when writing to Google Calendar. */
  timeZone: string;
}

export interface EventCandidate {
  /** The recognized original text, e.g. "10月20日下午3点" (Oct 20, 3 PM). */
  text: string;
  /** Surrounding context (shown to the user for confirmation). */
  context: string;
  deadline: boolean;
  allDay: boolean;
  start: string;
  end: string;
}

export interface EventSuggestion {
  event: EventDraft;
  /** All recognized times (sorted by likelihood, the first one is what event uses); empty = nothing recognized, and event is an all-day event tomorrow. */
  candidates: EventCandidate[];
}

export interface CalendarLink {
  email: string;
  createdAt: number;
  /** Most recent error (authorization expired, etc.); null = OK. */
  error: string | null;
}

export interface CalendarInfo {
  /** The server has Google OAuth configured, so Google Calendar can be connected. */
  google: boolean;
  /** Google calendar accounts in the old shape (kept for older clients; same accounts as `accounts` with provider google). */
  links: CalendarLink[];
  /** Calendar accounts (independent of mail accounts: the same Google account can be both, either, or neither). */
  accounts?: CalendarAccount[];
  /** Every calendar of every account, in display order. */
  calendars?: CalendarSource[];
}

// ---------------- Calendar accounts and events ----------------
//
// Momomail keeps no calendar of its own: every calendar belongs to an external account (Google Calendar, or any CalDAV server such as
// iCloud, Fastmail, Nextcloud, QQ / 163 mail). The server syncs them every few minutes and caches the events; clients read the cache
// and every edit is written to the provider first. Recurring events are shown (expanded per occurrence) but read-only for now.
// API: POST /api/calendar/caldav CaldavInput → CalendarAccount; DELETE /api/calendar/accounts/:id; POST /api/calendar/accounts/:id/sync;
//       PATCH /api/calendar/calendars/:id { hidden?, color? } → CalendarSource; GET /api/calendar/events?from=&to= → CalendarEvent[];
//       POST /api/calendar/items CalendarEventInput → CalendarEvent; PATCH /api/calendar/items/:id CalendarEventInput → CalendarEvent; DELETE /api/calendar/items/:id

export type CalendarProvider = 'google' | 'caldav';

export interface CalendarAccount {
  id: string;
  provider: CalendarProvider;
  /** Google account address, or the CalDAV user name. */
  email: string;
  /** Display name (defaults to the address / server host). */
  name: string;
  /** CalDAV server address (null for Google). */
  serverUrl: string | null;
  /** Last sync error (authorization expired, server unreachable); null = OK. */
  error: string | null;
  /** The error needs the user to sign in again (as opposed to a temporary failure). */
  needsAuth: boolean;
  lastSync: number | null;
  createdAt: number;
}

export interface CalendarSource {
  id: string;
  accountId: string;
  name: string;
  /** "#rrggbb": the user's choice, else the provider's color. */
  color: string;
  /** Can't add or change events (subscribed / shared read-only calendars, holidays). */
  readOnly: boolean;
  /** Hidden from the calendar view (still synced). */
  hidden: boolean;
  /** The account's main calendar (the default when adding an event). */
  primary: boolean;
}

export interface CalendarEvent {
  /** Stable for the same occurrence across syncs. */
  id: string;
  calendarId: string;
  title: string;
  location: string;
  description: string;
  /** All-day: start / end are 'YYYY-MM-DD' (end exclusive); otherwise UTC ISO strings. */
  allDay: boolean;
  start: string;
  end: string;
  /** One occurrence of a repeating event (read-only for now). */
  recurring: boolean;
  readOnly: boolean;
  /** Link to the event in the provider's web UI (Google only). */
  htmlLink: string | null;
}

export interface CalendarEventInput {
  calendarId: string;
  title: string;
  location: string;
  description: string;
  allDay: boolean;
  start: string;
  end: string;
  /** The user's time zone (IANA), written with timed events. */
  timeZone: string;
}

export interface CaldavInput {
  /** Server address (e.g. https://caldav.icloud.com); the calendars are discovered from it. */
  serverUrl: string;
  username: string;
  /** Password or app-specific password. */
  password: string;
  name?: string;
}

export interface CalendarAddResult {
  /** Which Google account's calendar it was written to. */
  calendar: string;
  /** URL of this event in Google Calendar. */
  htmlLink: string | null;
}

export interface RsvpResult {
  /** The reply email was sent. */
  sent: boolean;
  /** Google Calendar: updated = changed the status of an existing event; added = added it; null = calendar not connected or untouched. */
  calendar: 'updated' | 'added' | null;
  warning: string | null;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' or ISO → the compact Google / iCalendar format (all-day YYYYMMDD, otherwise UTC YYYYMMDDTHHMMSSZ). */
function compactTime(value: string, allDay: boolean): string {
  if (allDay) return value.slice(0, 10).replace(/-/g, '');
  const d = new Date(value);
  return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}Z`;
}

/** URL of Google Calendar's "new event" page (pre-filled, the user clicks save). No authorization needed. */
export function googleCalendarUrl(event: EventDraft): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${compactTime(event.start, event.allDay)}/${compactTime(event.end, event.allDay)}`,
  });
  if (event.description) params.set('details', event.description);
  if (event.location) params.set('location', event.location);
  if (event.timeZone) params.set('ctz', event.timeZone);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function icsText(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Fold lines at no more than 75 bytes each per RFC 5545. */
function icsFold(line: string): string {
  const out: string[] = [];
  let cur = '';
  let bytes = 0;
  for (const ch of line) {
    const n = new TextEncoder().encode(ch).length;
    if (bytes + n > (out.length ? 74 : 75)) {
      out.push(cur);
      cur = '';
      bytes = 0;
    }
    cur += ch;
    bytes += n;
  }
  out.push(cur);
  return out.join('\r\n ');
}

/** The .ics file for an event (import into iPhone Calendar, Outlook, etc.). */
export function eventIcs(event: EventDraft, uid: string, now = Date.now()): string {
  const when = (key: string, value: string) => (event.allDay ? `${key};VALUE=DATE:${compactTime(value, true)}` : `${key}:${compactTime(value, false)}`);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//momomail//momomail//ZH',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${compactTime(new Date(now).toISOString(), false)}`,
    when('DTSTART', event.start),
    when('DTEND', event.end),
    `SUMMARY:${icsText(event.title)}`,
    ...(event.location ? [`LOCATION:${icsText(event.location)}`] : []),
    ...(event.description ? [`DESCRIPTION:${icsText(event.description)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.map(icsFold).join('\r\n') + '\r\n';
}

export * from './i18n.js';
export * from './errors.js';
export * from './contacts.js';
